import { CardElement, Elements, useElements, useStripe } from "@stripe/react-stripe-js"
import { loadStripe } from "@stripe/stripe-js"
import { useMutation, useQuery } from "@tanstack/react-query"
import type {
  CustomerPurchaseCatalog,
  CustomerPurchaseCoupon,
  CustomerPurchaseProduct,
  CustomerPurchaseResponse,
} from "@ternary/contracts"
import { ThemeRoot } from "@ternary/ui"
import {
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { useTranslation } from "react-i18next"
import { Navigate, useLocation, useSearchParams } from "react-router-dom"
import { trackConsentedCustomerPage } from "../analytics.js"
import {
  CustomerApiError,
  completeCustomerPurchase,
  createCustomerPurchaseSetupIntent,
  loadCustomerPurchase,
  loadCustomerPurchaseCoupon,
  loadCustomerTenant,
} from "../api.js"
import stripePartnerBadge from "../assets/stripe-verified-partner.svg"
import { browserUuid } from "../browser-id.js"
import { useCardOptions } from "../components/card-options.js"
import { customerQueryKeys } from "../query-keys.js"
import { ConsentBanner } from "../signup/consent-banner.js"
import { NEUTRAL_BRAND } from "../signup/neutral-brand.js"
import { useThemeColor } from "../theme-color.js"
import { purchaseCountries, purchaseRegions } from "./purchase-locations.js"

interface DocusignClickWindow extends Window {
  docuSignClick?: {
    Clickwrap: {
      render(
        options: {
          accountId: string
          clickwrapId: string
          clientUserId: string
          documentData: { email: string; fullName: string }
          environment: string
          onAgreed(): void
          onDeclined(): void
          onError(error: unknown): void
        },
        selector: string,
      ): void
    }
  }
}

function money(amountMinor: number, currency: string, language: string) {
  return new Intl.NumberFormat(language, {
    currency,
    style: "currency",
  }).format(amountMinor / 100)
}

function newIdempotencyKey() {
  return browserUuid()
}

function discounted(amount: number, coupon: CustomerPurchaseCoupon | null) {
  if (!coupon) {
    return amount
  }
  if (coupon.percentOff !== null) {
    return Math.max(0, Math.round(amount * (1 - coupon.percentOff / 100)))
  }
  return Math.max(0, amount - (coupon.amountOffMinor ?? 0))
}

function PurchaseBrand({ catalog }: { catalog: CustomerPurchaseCatalog }) {
  return catalog.branding.logoUrl ? (
    <img
      alt={catalog.branding.name}
      className="legacy-purchase-logo"
      src={catalog.branding.logoUrl}
    />
  ) : (
    <span aria-label={catalog.branding.name} className="legacy-purchase-logo-fallback" role="img">
      {catalog.branding.name
        .split(/\s+/)
        .slice(0, 2)
        .map((part) => part[0])
        .join("")
        .toUpperCase()}
    </span>
  )
}

/** A checkout field: its visible label above the control. The placeholder stays as a hint. */
function PurchaseField({
  children,
  id,
  label,
}: {
  children: ReactNode
  id: string
  label: string
}) {
  return (
    <div className="legacy-purchase-field">
      <label htmlFor={id}>{label}</label>
      {children}
    </div>
  )
}

function LoggedOutPurchase({ catalog }: { catalog: CustomerPurchaseCatalog }) {
  const { i18n, t } = useTranslation()
  const product = catalog.products[0]
  if (!product) {
    return null
  }
  return (
    <main
      className="legacy-purchase-page"
      style={
        {
          "--purchase-background": catalog.branding.backgroundUrl
            ? `url("${catalog.branding.backgroundUrl}")`
            : catalog.branding.primaryColor,
          "--purchase-primary": catalog.branding.primaryColor,
        } as CSSProperties
      }
    >
      <section className="legacy-purchase-login-card">
        <div>
          <div className="legacy-purchase-product-lockup">
            <PurchaseBrand catalog={catalog} />
            <div>
              <h1>{catalog.branding.name}</h1>
              <strong>
                {money(product.priceMinor, product.currency, i18n.resolvedLanguage ?? "en")}
              </strong>
              <p>
                {t("Every {{count}} {{interval}}", {
                  count: product.intervalCount,
                  interval: t(product.interval.replace("_", " ")),
                })}
              </p>
            </div>
          </div>
          <ul className="legacy-purchase-features">
            {product.features.map((feature) => (
              <li key={feature}>{feature}</li>
            ))}
          </ul>
        </div>
        <div className="legacy-purchase-login-prompt">
          <h2>{t("Discord login required")}</h2>
          <p>
            {t(
              "Before accessing the checkout page you must connect your Discord account using the button below.",
            )}
          </p>
          <a
            className="legacy-purchase-discord-button"
            href={catalog.login.authorizationUrl ?? "/"}
          >
            {t("Login with Discord")}
          </a>
        </div>
      </section>
    </main>
  )
}

function Checkout({
  catalog,
  initialCoupon,
  slug,
}: {
  catalog: CustomerPurchaseCatalog
  initialCoupon: string
  slug: string | null
}) {
  const { i18n, t } = useTranslation()
  const elements = useElements()
  const stripe = useStripe()
  const [productId, setProductId] = useState(catalog.products[0]?.planId ?? "")
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [line1, setLine1] = useState("")
  const [line2, setLine2] = useState("")
  const [country, setCountry] = useState("US")
  const [state, setState] = useState("AL")
  const [city, setCity] = useState("")
  const [postalCode, setPostalCode] = useState("")
  const [agreed, setAgreed] = useState(false)
  const [couponCode, setCouponCode] = useState(initialCoupon)
  const [coupon, setCoupon] = useState<CustomerPurchaseCoupon | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [ignoreTrial, setIgnoreTrial] = useState(false)
  const [idempotencyKey, setIdempotencyKey] = useState(newIdempotencyKey)
  const [completedPurchase, setCompletedPurchase] = useState<CustomerPurchaseResponse | null>(null)
  const [cardFrame, cardOptions] = useCardOptions()
  const agreementAccepted = useRef(!catalog.agreement)
  const agreementClientUserId = useRef(browserUuid())
  const initialCouponApplied = useRef(false)
  const [agreementState, setAgreementState] = useState<
    "agreed" | "error" | "loading" | "presenting" | "ready"
  >(catalog.agreement ? "loading" : "agreed")
  const product = (catalog.products.find((candidate) => candidate.planId === productId) ??
    catalog.products[0]) as CustomerPurchaseProduct
  const regions =
    country === "CA" ? purchaseRegions.CA : country === "US" ? purchaseRegions.US : null
  const paid = Boolean(product && (product.priceMinor > 0 || product.initialPaymentMinor > 0))
  const setup = useQuery({
    enabled: paid && catalog.billing.paymentProvider === "stripe",
    queryFn: ({ signal }) => createCustomerPurchaseSetupIntent(slug, { signal }),
    queryKey: customerQueryKeys.purchaseSetup(slug),
    retry: false,
  })
  const couponMutation = useMutation({
    mutationFn: () => loadCustomerPurchaseCoupon(slug, product?.productId ?? "", couponCode.trim()),
    onSuccess(value) {
      setCoupon(value)
      setFormError(null)
    },
  })
  const purchase = useMutation({
    mutationFn: (input: {
      idempotencyKey: string
      purchase: Parameters<typeof completeCustomerPurchase>[2]
      slug: string | null
    }) => completeCustomerPurchase(input.slug, input.idempotencyKey, input.purchase),
  })

  useEffect(() => {
    if (!initialCoupon || initialCouponApplied.current || !product) {
      return
    }
    initialCouponApplied.current = true
    couponMutation.mutate()
  }, [couponMutation, initialCoupon, product])

  useEffect(() => {
    const agreement = catalog.agreement
    if (!agreement) {
      agreementAccepted.current = true
      setAgreementState("agreed")
      return
    }
    agreementAccepted.current = false
    const clickWindow = window as DocusignClickWindow
    if (clickWindow.docuSignClick?.Clickwrap) {
      setAgreementState("ready")
      return
    }
    const id = "ternary-docusign-click"
    const existing = document.getElementById(id)
    const script =
      existing instanceof HTMLScriptElement ? existing : document.createElement("script")
    const ready = () => {
      setAgreementState(
        (window as DocusignClickWindow).docuSignClick?.Clickwrap ? "ready" : "error",
      )
    }
    const failed = () => setAgreementState("error")
    script.addEventListener("load", ready)
    script.addEventListener("error", failed)
    if (!existing) {
      script.async = true
      script.id = id
      script.src = `${agreement.environmentUrl}/clickapi/sdk/latest/docusign-click.js`
      document.head.appendChild(script)
    }
    return () => {
      script.removeEventListener("load", ready)
      script.removeEventListener("error", failed)
    }
  }, [catalog.agreement])

  const taxMinor = Math.round(product.priceMinor * (catalog.billing.taxPercentage / 100))
  const recurringMinor = discounted(product.priceMinor + taxMinor, coupon)
  const dueTodayMinor =
    product.trialDays > 0 && !ignoreTrial
      ? product.initialPaymentMinor
      : discounted(product.priceMinor + taxMinor + product.initialPaymentMinor, coupon)

  function selectProduct(planId: string) {
    setProductId(planId)
    setCoupon(null)
    setCouponCode("")
    setIgnoreTrial(false)
    setFormError(null)
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setFormError(null)
    if (couponCode && !coupon) {
      setFormError("Please apply the coupon entered.")
      return
    }
    if (!agreed) {
      setFormError("Please agree to the Terms of Service.")
      return
    }
    if (catalog.agreement && !agreementAccepted.current) {
      const clickwrap = (window as DocusignClickWindow).docuSignClick?.Clickwrap
      if (!clickwrap) {
        setAgreementState("error")
        setFormError("The purchase agreement could not be loaded. Please try again.")
        return
      }
      const form = event.currentTarget
      setAgreementState("presenting")
      try {
        clickwrap.render(
          {
            accountId: catalog.agreement.accountId,
            clickwrapId: catalog.agreement.clickwrapId,
            clientUserId: `${email.trim().toLowerCase()}-${agreementClientUserId.current}`.slice(
              0,
              128,
            ),
            documentData: {
              email: email.trim().toLowerCase(),
              fullName: `${firstName} ${lastName}`.trim(),
            },
            environment: catalog.agreement.environmentUrl,
            onAgreed() {
              agreementAccepted.current = true
              setAgreementState("agreed")
              setFormError(null)
              queueMicrotask(() => form.requestSubmit())
            },
            onDeclined() {
              setAgreementState("ready")
              setFormError("You must accept the purchase agreement before subscribing.")
            },
            onError() {
              setAgreementState("error")
              setFormError("The purchase agreement could not be completed. Please try again.")
            },
          },
          "#ternary-docusign-clickwrap",
        )
      } catch {
        setAgreementState("error")
        setFormError("The purchase agreement could not be loaded. Please try again.")
      }
      return
    }
    let paymentMethodId = setup.data?.fixturePaymentMethodId ?? null
    if (paid && !paymentMethodId) {
      const card = elements?.getElement(CardElement)
      if (!stripe || !card || !setup.data) {
        setFormError("Secure card entry is still loading.")
        return
      }
      const confirmed = await stripe.confirmCardSetup(setup.data.clientSecret, {
        payment_method: { card },
      })
      if (confirmed.error) {
        setFormError(confirmed.error.message ?? "The card could not be verified.")
        return
      }
      paymentMethodId =
        typeof confirmed.setupIntent.payment_method === "string"
          ? confirmed.setupIntent.payment_method
          : (confirmed.setupIntent.payment_method?.id ?? null)
    }
    try {
      const result = await purchase.mutateAsync({
        idempotencyKey,
        purchase: {
          address: catalog.billing.requireAddress
            ? {
                city,
                country,
                line1,
                line2,
                postalCode,
                state,
              }
            : null,
          agreedToTerms: true,
          coupon: coupon?.code ?? null,
          email,
          firstName,
          ignoreTrialEligibility: ignoreTrial,
          lastName,
          paymentMethodId,
          phone,
          planId: product.planId,
          slug,
        },
        slug,
      })
      if (result.requiresAction && result.clientSecret) {
        if (!stripe) {
          setFormError("Stripe authentication could not start.")
          return
        }
        const confirmed = await stripe.confirmCardPayment(result.clientSecret)
        if (confirmed.error) {
          setFormError(confirmed.error.message ?? "Stripe authentication failed.")
          return
        }
      }
      setCompletedPurchase(result)
    } catch (error) {
      if (!(error instanceof Error) || error.message !== "Trial already purchased") {
        setIdempotencyKey(newIdempotencyKey())
      }
    }
  }

  const purchaseError = purchase.error instanceof Error ? purchase.error.message : null
  const error =
    formError ??
    (couponMutation.error instanceof Error
      ? couponMutation.error.message
      : setup.error instanceof Error
        ? setup.error.message
        : purchaseError)

  if (completedPurchase) {
    return (
      <main
        className="legacy-purchase-page"
        style={
          {
            "--purchase-primary": catalog.branding.primaryColor,
          } as CSSProperties
        }
      >
        <section className="legacy-purchase-success" role="status">
          <PurchaseBrand catalog={catalog} />
          <h1>{t("Purchase complete")}</h1>
          <p>{t(completedPurchase.purchaseMessage || "Your membership is ready.")}</p>
          <strong>{completedPurchase.key}</strong>
          <a href={completedPurchase.redirect}>{t("Continue")}</a>
        </section>
      </main>
    )
  }

  return (
    <main
      className="legacy-purchase-page"
      style={
        {
          "--purchase-background": catalog.branding.backgroundUrl
            ? `url("${catalog.branding.backgroundUrl}")`
            : catalog.branding.primaryColor,
          "--purchase-primary": catalog.branding.primaryColor,
        } as CSSProperties
      }
    >
      <form className="legacy-purchase-form" onSubmit={submit}>
        <div className="legacy-purchase-fields">
          <h1>{catalog.branding.name}</h1>
          <PurchaseField id="purchase-membership" label={t("Membership")}>
            <select
              id="purchase-membership"
              onChange={(event) => selectProduct(event.target.value)}
              required
              value={product.planId}
            >
              {catalog.products.map((candidate) => (
                <option key={candidate.planId} value={candidate.planId}>
                  {candidate.name}
                </option>
              ))}
            </select>
          </PurchaseField>
          <div className="legacy-purchase-split">
            <PurchaseField id="purchase-first-name" label={t("First Name")}>
              <input
                id="purchase-first-name"
                onChange={(event) => setFirstName(event.target.value)}
                placeholder={t("First Name")}
                required
                value={firstName}
              />
            </PurchaseField>
            <PurchaseField id="purchase-last-name" label={t("Last Name")}>
              <input
                id="purchase-last-name"
                onChange={(event) => setLastName(event.target.value)}
                placeholder={t("Last Name")}
                required
                value={lastName}
              />
            </PurchaseField>
          </div>
          <PurchaseField id="purchase-email" label={t("Email")}>
            <input
              id="purchase-email"
              onChange={(event) => {
                setEmail(event.target.value)
                if (catalog.agreement && agreementAccepted.current) {
                  agreementAccepted.current = false
                  agreementClientUserId.current = browserUuid()
                  setAgreementState("ready")
                }
              }}
              placeholder={t("Email")}
              required
              type="email"
              value={email}
            />
          </PurchaseField>
          {(catalog.billing.collectPhone || catalog.billing.requireAddress) && (
            <PurchaseField id="purchase-phone" label={t("Phone")}>
              <input
                id="purchase-phone"
                onChange={(event) => setPhone(event.target.value)}
                placeholder={t("Phone")}
                required
                type="tel"
                value={phone}
              />
            </PurchaseField>
          )}
          {catalog.billing.requireAddress ? (
            <>
              <PurchaseField id="purchase-address" label={t("Address")}>
                <input
                  id="purchase-address"
                  onChange={(event) => setLine1(event.target.value)}
                  placeholder={t("Address")}
                  required
                  value={line1}
                />
              </PurchaseField>
              <PurchaseField id="purchase-address-2" label={t("Address line 2")}>
                <input
                  id="purchase-address-2"
                  onChange={(event) => setLine2(event.target.value)}
                  placeholder={t("Address line 2")}
                  value={line2}
                />
              </PurchaseField>
              <div className="legacy-purchase-split">
                <PurchaseField id="purchase-country" label={t("Country")}>
                  <select
                    id="purchase-country"
                    onChange={(event) => {
                      setCountry(event.target.value)
                      setState(
                        event.target.value === "CA"
                          ? "AB"
                          : event.target.value === "US"
                            ? "AL"
                            : "",
                      )
                    }}
                    value={country}
                  >
                    {purchaseCountries.map(([code, name]) => (
                      <option key={code} value={code}>
                        {name}
                      </option>
                    ))}
                  </select>
                </PurchaseField>
                {regions ? (
                  <PurchaseField
                    id="purchase-state"
                    label={country === "CA" ? t("Province") : t("State")}
                  >
                    <select
                      id="purchase-state"
                      onChange={(event) => setState(event.target.value)}
                      required
                      value={state}
                    >
                      {regions.map(([code, name]) => (
                        <option key={code} value={code}>
                          {name}
                        </option>
                      ))}
                    </select>
                  </PurchaseField>
                ) : (
                  <PurchaseField id="purchase-state" label={t("State / Province / Region")}>
                    <input
                      id="purchase-state"
                      onChange={(event) => setState(event.target.value)}
                      placeholder={t("State / Province / Region")}
                      required
                      value={state}
                    />
                  </PurchaseField>
                )}
              </div>
              <div className="legacy-purchase-split">
                <PurchaseField id="purchase-city" label={t("City")}>
                  <input
                    id="purchase-city"
                    onChange={(event) => setCity(event.target.value)}
                    placeholder={t("City")}
                    required
                    value={city}
                  />
                </PurchaseField>
                <PurchaseField id="purchase-zip" label={t("Zip code")}>
                  <input
                    id="purchase-zip"
                    onChange={(event) => setPostalCode(event.target.value)}
                    placeholder={t("Zip code")}
                    required
                    value={postalCode}
                  />
                </PurchaseField>
              </div>
            </>
          ) : null}
          {paid ? (
            catalog.billing.publishableKey ? (
              <div className="legacy-purchase-card" ref={cardFrame}>
                <CardElement {...(cardOptions ? { options: cardOptions } : {})} />
              </div>
            ) : (
              <fieldset
                aria-label={t("Fixture payment card")}
                className="legacy-purchase-card-fixture"
              >
                <span>4242 4242 4242 4242</span>
                <span>12 / 34</span>
                <span>123</span>
              </fieldset>
            )
          ) : null}
          <PurchaseField id="purchase-coupon" label={t("Coupon Code or Referral Code")}>
            <div className="legacy-purchase-coupon">
              <input
                id="purchase-coupon"
                onChange={(event) => {
                  setCouponCode(event.target.value)
                  setCoupon(null)
                }}
                placeholder={t("Coupon Code or Referral Code")}
                value={couponCode}
              />
              <button
                disabled={!couponCode.trim() || couponMutation.isPending}
                onClick={() => couponMutation.mutate()}
                type="button"
              >
                {t("Apply")}
              </button>
            </div>
          </PurchaseField>
          <label className="legacy-purchase-agreement">
            <input
              checked={agreed}
              onChange={(event) => setAgreed(event.target.checked)}
              type="checkbox"
            />
            <span>
              {t("By clicking this box and subscribing you are agreeing to our")}
              <br />
              <a href={catalog.branding.termsUrl} rel="noreferrer" target="_blank">
                {t("Terms of Service")}
              </a>
            </span>
          </label>
          {catalog.agreement ? (
            <div aria-live="polite" id="ternary-docusign-clickwrap">
              {agreementState === "loading" ? (
                <p className="legacy-purchase-agreement-status">
                  {t("Secure agreement is loading…")}
                </p>
              ) : agreementState === "error" ? (
                <p className="legacy-purchase-agreement-status is-error">
                  {t("Secure agreement is unavailable.")}
                </p>
              ) : null}
            </div>
          ) : null}
          {error ? (
            <div className="legacy-purchase-error" role="alert">
              <p>{t(error)}</p>
              {purchaseError === "Trial already purchased" && !ignoreTrial ? (
                <button
                  onClick={() => {
                    setIgnoreTrial(true)
                    setIdempotencyKey(newIdempotencyKey())
                    purchase.reset()
                  }}
                  type="button"
                >
                  {t("Continue without free trial")}
                </button>
              ) : null}
            </div>
          ) : null}
          {catalog.billing.paymentProvider === "unavailable" ? (
            <div className="legacy-purchase-error" role="alert">
              {t("Billing is not configured for this checkout.")}
            </div>
          ) : null}
          <button
            className="legacy-purchase-submit"
            disabled={
              purchase.isPending ||
              agreementState === "loading" ||
              agreementState === "presenting" ||
              (paid && setup.isFetching) ||
              catalog.billing.paymentProvider === "unavailable"
            }
            type="submit"
          >
            {purchase.isPending ? "…" : t("Subscribe")}
          </button>
          {catalog.billing.paymentProvider === "stripe" ? (
            <a
              className="legacy-purchase-stripe-badge"
              href="https://stripe.com/partners/ternary-developers"
              rel="noreferrer"
              target="_blank"
            >
              <img alt={t("Stripe Verified Partner")} src={stripePartnerBadge} />
            </a>
          ) : null}
        </div>
        <aside className="legacy-purchase-summary">
          <h2>{t("Order Summary")}</h2>
          <div className="legacy-purchase-summary-product">
            <PurchaseBrand catalog={catalog} />
            <h3>{product.name}</h3>
          </div>
          {paid ? (
            <>
              <dl>
                <div className="legacy-purchase-summary-row">
                  <dt>{t("Subtotal")}</dt>
                  <dd>
                    {money(product.priceMinor, product.currency, i18n.resolvedLanguage ?? "en")}
                  </dd>
                </div>
                {catalog.billing.taxPercentage > 0 ? (
                  <div className="legacy-purchase-summary-row">
                    <dt>{t("Tax rate")}</dt>
                    <dd>{catalog.billing.taxPercentage}%</dd>
                  </div>
                ) : null}
              </dl>
              {coupon ? (
                <p className="legacy-purchase-discount">
                  {t("Applied Coupon")}:{" "}
                  {coupon.percentOff !== null
                    ? `${coupon.percentOff}%`
                    : money(
                        coupon.amountOffMinor ?? 0,
                        product.currency,
                        i18n.resolvedLanguage ?? "en",
                      )}
                </p>
              ) : null}
              {product.trialDays > 0 && !ignoreTrial ? (
                <p className="legacy-purchase-trial">
                  {product.trialDays} {t("Free Trial Days Applied")}
                </p>
              ) : null}
              <hr />
              <dl>
                <div className="legacy-purchase-summary-row">
                  <dt>
                    <strong>{t("Due Today")}</strong>
                  </dt>
                  <dd>
                    <strong>
                      {money(dueTodayMinor, product.currency, i18n.resolvedLanguage ?? "en")}
                    </strong>
                  </dd>
                </div>
                {product.interval !== "one_time" ? (
                  <div className="legacy-purchase-summary-row">
                    <dt>
                      <strong>{t("Next Invoice")}</strong>
                    </dt>
                    <dd>
                      <strong>
                        {money(recurringMinor, product.currency, i18n.resolvedLanguage ?? "en")}
                      </strong>
                    </dd>
                  </div>
                ) : null}
              </dl>
            </>
          ) : (
            <>
              <ul className="legacy-purchase-features">
                {product.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>
              <hr />
              <p className="legacy-purchase-free">
                <strong>{t("Due Today")}</strong>
                <strong>{t("FREE")}</strong>
              </p>
            </>
          )}
        </aside>
      </form>
    </main>
  )
}

export function PurchasePage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { search } = useLocation()
  const slug = searchParams.get("password")?.trim() || null
  const initialCoupon = searchParams.get("coupon")?.trim().slice(0, 128) ?? ""
  const catalog = useQuery({
    queryFn: ({ signal }) => loadCustomerPurchase(slug, { signal }),
    queryKey: customerQueryKeys.purchase(slug),
  })
  // A bare address whose checkout is unavailable (404) opens the business's published signup page
  // instead. A working checkout stays, a failed one (server or network) keeps today's error page,
  // and a checkout link (with its password) never asks the tenant; a tenant that cannot load, or
  // names no page, keeps today's page.
  const signupFallback =
    slug === null && catalog.error instanceof CustomerApiError && catalog.error.status === 404
  const tenant = useQuery({
    enabled: signupFallback,
    queryFn: ({ signal }) => loadCustomerTenant({ signal }),
    queryKey: customerQueryKeys.tenant,
  })
  const signupSlug = signupFallback ? (tenant.data?.signupSlug ?? null) : null
  useThemeColor(catalog.data?.branding.primaryColor)
  useEffect(() => {
    if (!catalog.data) {
      return
    }
    trackConsentedCustomerPage(catalog.data.analytics, catalog.data.conversionEvent)
  }, [catalog.data])
  const stripePromise = useMemo(
    () =>
      catalog.data?.billing.publishableKey
        ? loadStripe(
            catalog.data.billing.publishableKey,
            catalog.data.billing.stripeAccountId
              ? { stripeAccount: catalog.data.billing.stripeAccountId }
              : {},
          )
        : null,
    [catalog.data?.billing.publishableKey, catalog.data?.billing.stripeAccountId],
  )

  // In place, with the link's query (its utm_* and coupon), so Back never returns to this address.
  if (signupSlug) {
    return <Navigate replace to={{ pathname: `/signup/${signupSlug}`, search }} />
  }
  // Before the catalog answers, the tenant is not known: the neutral brand, never Ternary's green.
  // A refused bare address waits for the tenant too, so it never flashes "Product unavailable!".
  if (catalog.isPending || (signupFallback && tenant.isPending)) {
    return (
      <ThemeRoot brand={NEUTRAL_BRAND}>
        <main aria-busy="true" className="legacy-purchase-state">
          <span className="loading-mark" />
          <p>{t("Loading checkout…")}</p>
        </main>
      </ThemeRoot>
    )
  }
  if (catalog.isError || !catalog.data) {
    return (
      <ThemeRoot brand={NEUTRAL_BRAND}>
        <main className="legacy-purchase-state">
          <h1>{t("Product unavailable!")}</h1>
          <p>
            {t(catalog.error instanceof Error ? catalog.error.message : "Checkout could not load.")}
          </p>
          <a href="/dashboard">{t("Return to dashboard")}</a>
        </main>
      </ThemeRoot>
    )
  }
  const { analytics, conversionEvent } = catalog.data
  // Pixels load only after the visitor accepts ad cookies (R64).
  const banner = (
    <ConsentBanner
      note={t("Declining does not affect your order.")}
      onChoice={(choice) => {
        if (choice.ads) trackConsentedCustomerPage(analytics, conversionEvent)
      }}
      privacyUrl="/privacy"
    />
  )
  // One theme root in the tenant's brand holds the page and its cookie banner, so both are themed.
  // It is never keyed and nothing gates on the brand: Checkout, the only child of <Elements>, owns
  // the setup intent, and remounting it would ask for a second one.
  const brand = catalog.data.branding.primaryColor
  if (catalog.data.login.required && !catalog.data.login.satisfied) {
    return (
      <ThemeRoot brand={brand}>
        <LoggedOutPurchase catalog={catalog.data} />
        {banner}
      </ThemeRoot>
    )
  }
  return (
    <ThemeRoot brand={brand}>
      <Elements stripe={stripePromise}>
        <Checkout catalog={catalog.data} initialCoupon={initialCoupon} slug={slug} />
      </Elements>
      {banner}
    </ThemeRoot>
  )
}
